import { useEffect, cloneElement, useMemo, useRef, useState } from 'react';

import sortBy from 'lodash/sortBy';
import * as Recharts from 'recharts';
import { useTheme, blend } from '@gogame/ui/styles';
import MetricValueFormatter from '../../MetricValueFormatter';
import Text from '../../Text';
import { Area, Bar, Line, Scatter } from '../../cartesian/index';
import { getLineModeDasharray } from '../../utils';
import BreakDot from '../BreakDot';
import { HIDDEN_Y_AXIS_ID, VALUE_TYPE_PERCENT } from '../constants';

const TYPES_ORDER = [Area, Bar, Scatter, Line];
const sortByType = (m) => TYPES_ORDER.indexOf(m.type);

const HEIGHT = 11;
const ScatterCustomizedLabel = ({ formatter, value, ...props }) => {
  const { x: xProp, width, y: yProp } = props;
  const textRef = useRef(null);

  const [offset, setOffset] = useState(0);
  const x = xProp + offset + width / 2;
  let y = yProp - 8;

  if (y < HEIGHT) {
    y = yProp + 20;
  }

  useEffect(() => {
    if (textRef.current) {
      const textBBox = textRef.current.getBBox();
      if (textBBox.x < 0) {
        setOffset(Math.abs(textBBox.x));
      }
    }
  }, []);
  const theme = useTheme();

  if (value === undefined) {
    return null;
  }

  return (
    <Text
      {...props}
      x={x}
      y={y}
      textAnchor={'middle'}
      dominantBaseline={'middle'}
      ref={textRef}
      strokeWidth={1}
      fill={theme.palette.text.primary}
    >
      {formatter
        ? cloneElement(formatter, {
            value: value,
          })
        : value}
    </Text>
  );
};

const Label = ({ formatter, x, y, height, width, dataKey, data, index }) => {
  const labelValue = data[index][dataKey];
  const theme = useTheme();
  return (
    <Text
      x={x + width / 2}
      y={y + height / 2}
      textAnchor={'middle'}
      dominantBaseline={'middle'}
      fill={theme.palette.text.primary}
    >
      {formatter
        ? cloneElement(formatter, {
            value: labelValue,
          })
        : labelValue}
    </Text>
  );
};

export const useMetricsElementsMemo = ({
  metrics,
  colorGetter,
  hiddenMetrics,
  ownAxisMetrics,
  hoveredMetric,
  data,
  percentage,
}) => {
  return useMemo(() => {
    const metricElements = metrics.map(
      ({
        type,
        dataKey,
        colorKey,
        dashed,
        yAxisGroup,
        name,
        valueType,
        id,
        mode,
        stackId,
        secondary,
      }) => {
        const hidden = hiddenMetrics.includes(id);
        const withLabel = metrics.some(({ id: metricId, label }) => {
          return metricId === id && label;
        });
        const color =
          colorKey !== undefined ? colorGetter(colorKey) : undefined;

        const yAxisId = ownAxisMetrics.includes(id)
          ? id
          : yAxisGroup !== undefined
            ? hidden
              ? HIDDEN_Y_AXIS_ID
              : yAxisGroup
            : undefined;

        const hasHover = hoveredMetric !== null;
        const hovered = hoveredMetric === id;
        const highlighted = hovered || !hasHover;
        const formatter = <MetricValueFormatter type={valueType} />;
        const percentFormatter = (
          <MetricValueFormatter type={VALUE_TYPE_PERCENT} />
        );
        const labelFormatter = percentage ? percentFormatter : formatter;
        const labelEl =
          withLabel && !hidden ? (
            <Label data={data} dataKey={dataKey} formatter={labelFormatter} />
          ) : null;

        switch (type) {
          case 'scatter': {
            const opacity = hidden ? 0 : highlighted ? 1 : 0.3;

            return (
              <Scatter
                id={id}
                key={id}
                name={name}
                yAxisId={yAxisId}
                dataKey={dataKey}
                fill={color}
                stroke={color}
                formatter={formatter}
                fillOpacity={opacity}
                strokeOpacity={opacity} // use by tooltip to filter visible
                shape={dashed ? 'cross' : 'circle'}
                strokeDasharray={getLineModeDasharray({ dashed, mode })}
              >
                {withLabel && !hidden ? (
                  <Recharts.LabelList
                    dataKey={dataKey}
                    content={
                      <ScatterCustomizedLabel formatter={labelFormatter} />
                    }
                  />
                ) : null}
              </Scatter>
            );
          }
          case 'area':
            const lineModeStyles = getLineModeDasharray({ mode, dashed });
            const opacity = hidden ? 0 : highlighted ? 1 : 0.3;

            return (
              <Area
                id={id}
                key={id}
                name={name}
                dataKey={dataKey}
                yAxisId={yAxisId}
                stackId={stackId}
                formatter={formatter}
                activeDot={!hidden}
                dot={(p) => <BreakDot {...p} chartData={data} />}
                fill={lineModeStyles ? 'none' : color}
                fillOpacity={opacity}
                strokeOpacity={opacity}
                stroke={color}
                strokeWidth={hovered ? 3 : 2}
                strokeDasharray={lineModeStyles}
              />
            );
          case 'bar': {
            const opacity = hidden ? 0 : highlighted ? 1 : 0.3;

            return (
              <Bar
                id={id}
                key={id}
                name={name}
                maxBarSize={150}
                yAxisId={yAxisId}
                dataKey={dataKey}
                stackId={stackId}
                strokeWidth={3}
                strokeOpacity={opacity}
                fillOpacity={opacity}
                fill={color}
                formatter={formatter}
                label={hidden ? undefined : labelEl}
                stroke={dashed ? 'white' : color}
                strokeDasharray={dashed ? '4' : undefined}
              />
            );
          }
          default: {
            return (
              <Line
                key={id}
                stroke={color}
                dataKey={dataKey}
                id={id}
                yAxisId={yAxisId}
                dot={(p) => <BreakDot {...p} chartData={data} />}
                name={name}
                activeDot={!hidden}
                strokeOpacity={
                  hidden
                    ? 0
                    : secondary
                      ? highlighted
                        ? 0.6
                        : 0.3
                      : highlighted
                        ? 1
                        : 0.3
                }
                strokeWidth={hovered ? 4 : 2}
                formatter={formatter}
                strokeDasharray={getLineModeDasharray({ dashed, mode })}
              />
            );
          }
        }
      }
    );

    return sortBy(metricElements, [sortByType]);
  }, [
    hiddenMetrics,
    hoveredMetric,
    metrics,
    colorGetter,
    percentage,
    ownAxisMetrics,
  ]);
};

const getPath = (x, y, width, height) => {
  return `M${x},${y + height}C${x + width},${y + height} ${x + width},${y + height / 3}
  ${x + width}, ${y}
  C${x + width / 2},${y + height / 3} ${x + (2 * width) / 3},${y + height} ${x + width}, ${y + height}
  Z`;
};

const TriangleBar = (props) => {
  const { fill, x, y, width, height } = props;
  return <path d={getPath(x, y, width, height)} stroke="none" fill={fill} />;
};
